import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Swal from 'sweetalert2';
import CandidateRow from './CandidateRow';
import * as request from '../../../../../Common/Util/Request';
import { formatOfflineScheduleLabel } from '../../../../../Common/Util/Schedule';
import { getTxStatusText } from '../../../../../Common/Util/Transaction';
import './index.scss';

const REASON_MAX_LENGTH = 500;

/**
 * preConfirm 실패 시 되돌리는 센티널.
 *
 * sweetalert2 11.7.0 은 preConfirm 이 **정확히 `false`** 를 반환하면 팝업의 로딩만 끄고
 * 바깥 프로미스를 resolve 하지 않는다 (dist/sweetalert2.js confirm() 의
 * `preConfirmValue === false` 분기가 succeedWith 를 건너뛰고 handleAwaitingPromise 만 호출).
 * 그러면 아래 .then / .finally 가 영영 실행되지 않아
 *   - onReload() 가 호출되지 않고 (후보 목록이 갱신되지 않음)
 *   - submitting 이 true 로 고정돼 취소 버튼이 비활성 + handleClose 가 early-return → 모달에 갇힌다.
 * 실패도 "값이 있는 결과"로 되돌려 프로미스를 정상 종료시킨다.
 */
const SUBMIT_FAILED = Object.freeze({ __optionChangeFailed: true });

/**
 * 수강 옵션 변경 모달.
 *
 * - 후보 판정(changeable / reasonText)은 전부 서버 응답을 그대로 사용한다.
 * - 실패 시 axios 인터셉터(Request.js)가 error.response.data.detail 을 alert 으로 이미 노출하므로
 *   Swal.showValidationMessage 를 중복 호출하지 않는다 (경고 2회 노출 방지).
 */
function OptionChangeModal(props) {
  const { open, data, userClassId, onClose, onSuccess, onReload } = props;
  const intl = useIntl();

  const [selectedId, setSelectedId] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 모달이 열릴 때마다(그리고 후보 재조회 시) 선택 상태를 초기화한다.
  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setSubmitting(false);
    }
  }, [open, data]);

  const candidates = useMemo(() => (data && data.candidates) || [], [data]);
  const current = data && data.current;

  const reasonText = reason.trim();
  const canSubmit = !!selectedId && reasonText.length > 0 && reason.length <= REASON_MAX_LENGTH && !submitting;

  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => candidate.id === selectedId),
    [candidates, selectedId],
  );

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    if (reasonText.length > 0) {
      Swal.fire({
        icon: 'warning',
        text: '입력한 변경 사유가 사라집니다. 닫으시겠습니까?',
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '닫기',
        reverseButtons: true,
        customClass: { container: 'option-change-swal' },
      }).then((result) => {
        if (result.isConfirmed) {
          setReason('');
          onClose();
        }
      });
      return;
    }
    setReason('');
    onClose();
  }, [onClose, reasonText, submitting]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit || !selectedCandidate) {
      return;
    }
    // 라벨은 서버 제공값을 우선 사용한다(이력 스냅샷과 동일 포맷).
    const label = selectedCandidate.label || formatOfflineScheduleLabel(selectedCandidate, intl);
    setSubmitting(true);
    Swal.fire({
      title: `수강 옵션을 "${label}"으로 변경하시겠습니까?`,
      text: '확인 시 즉시 반영됩니다. 결제 금액은 조정되지 않으며, 수강생에게 별도 안내는 발송되지 않습니다.',
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
      reverseButtons: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      // MUI Dialog(z-index 1300) 위에 떠야 하므로 z-index 를 끌어올린다.
      customClass: { container: 'option-change-swal' },
      preConfirm: async () => {
        try {
          const result = await request.updateUserClassOption(userClassId, {
            scheduleId: selectedId,
            reason: reasonText,
          });
          if (result && result.ok && result.data) {
            return result.data;
          }
          return SUBMIT_FAILED;
        } catch (error) {
          // 인터셉터가 detail 을 alert 으로 노출한다. 여기서 추가 노출하지 않는다.
          return SUBMIT_FAILED;
        }
      },
    })
      .then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        if (result.value && result.value !== SUBMIT_FAILED) {
          setReason('');
          onSuccess(result.value);
          return;
        }
        // 실패(대부분 경합) → 모달은 유지하고 후보 목록만 최신화한다.
        if (onReload) {
          onReload();
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [canSubmit, selectedCandidate, intl, userClassId, selectedId, reasonText, onSuccess, onReload]);

  if (!data) {
    return null;
  }

  const currentOccupied = current ? (current.student_count || 0) + (current.student_ready_count || 0) : 0;

  // 출석부 파이프라인 양쪽이 Transaction.status = PAID INNER JOIN 이라
  // 결제 완료 전 수강생은 옵션을 바꿔도 출결 목록에 나타나지 않는다 (기존 동작, 차단하지 않고 안내만 한다).
  //
  // 문구는 서버가 attendanceNotice.noticeText 로 내려준다. 어드민에서 문구를 만들지 않는다.
  // transaction 은 null 일 수 있으므로(Transaction 이 paranoid — 소프트 삭제된 결제는 include 가 null)
  // 반드시 최상위 attendanceNotice 를 읽는다.
  const attendanceNoticeText = data.attendanceNotice?.noticeText || null;

  return (
    <Dialog className="option-change-modal" open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>수강 옵션 변경</DialogTitle>
      <DialogContent dividers>
        <section className="option-change-modal__summary">
          <div>
            <span className="option-change-modal__label">회원</span>
            <span>
              {data.user?.name || '-'} ({data.user?.nickname || '-'}, ID {data.user?.id})
            </span>
          </div>
          <div>
            <span className="option-change-modal__label">강의</span>
            <span>
              [{data.class?.id}] {data.class?.title}
            </span>
          </div>
          <div>
            <span className="option-change-modal__label">결제</span>
            <span>
              {/* Transaction 은 paranoid — 소프트 삭제된 결제는 null 로 내려온다. */}
              {data.transaction
                ? `${getTxStatusText(data.transaction)} · ${(data.transaction.paid_amount || 0).toLocaleString()}원`
                : '결제 정보 없음 (삭제된 결제)'}
            </span>
          </div>
        </section>

        <h4 className="option-change-modal__section-title">현재 옵션</h4>
        <section className="option-change-modal__current">
          {current ? (
            <>
              <div className="option-change-modal__current-title">
                {current.label || formatOfflineScheduleLabel(current, intl)}
                {/* TINYINT(0/1) 로 내려오는 경우에도 화면에 "0" 이 찍히지 않도록 불리언으로 강제한다. */}
                {!!current.option_price_is_promotion && (
                  <Chip label="프로모션" size="small" color="warning" sx={{ marginLeft: '0.5rem' }} />
                )}
              </div>
              <div className="option-change-modal__current-sub">
                {[
                  current.class_location,
                  `정원 ${current.student_limit ? `${currentOccupied}/${current.student_limit}` : '제한 없음'}`,
                  `옵션가 ${(current.option_price || 0).toLocaleString()}원`,
                ]
                  .filter((part) => !!part)
                  .join(' · ')}
              </div>
            </>
          ) : (
            <div className="option-change-modal__current-sub">현재 지정된 옵션이 없습니다.</div>
          )}
        </section>

        <h4 className="option-change-modal__section-title">변경할 옵션 선택</h4>
        <div className="option-change-modal__table-wrap">
          <table className="option-change-modal__table">
            <thead>
              <tr>
                <th />
                <th>옵션</th>
                <th>기간</th>
                <th>정원</th>
                <th>가격 차이</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate) => (
                <CandidateRow
                  key={candidate.id}
                  candidate={candidate}
                  selected={candidate.id === selectedId}
                  onSelect={setSelectedId}
                />
              ))}
            </tbody>
          </table>
        </div>

        <p className="option-change-modal__notice">
          <InfoOutlinedIcon fontSize="small" />
          결제 금액은 조정되지 않습니다. 차액 정산이 필요하면 별도로 처리해 주세요.
        </p>
        {attendanceNoticeText && (
          <p className="option-change-modal__notice option-change-modal__notice--warn">
            <WarningAmberIcon fontSize="small" />
            {attendanceNoticeText}
          </p>
        )}

        <h4 className="option-change-modal__section-title">변경 사유 (필수)</h4>
        <TextField
          className="option-change-modal__reason"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX_LENGTH))}
          multiline
          minRows={2}
          fullWidth
          size="small"
          placeholder="예) 수강생 개인 일정 변경 요청 (유선 확인)"
          error={reason.length > 0 && reasonText.length === 0}
          helperText={
            reasonText.length === 0 ? '변경 사유를 입력해 주세요.' : `${reason.length} / ${REASON_MAX_LENGTH}`
          }
          inputProps={{ maxLength: REASON_MAX_LENGTH }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          취소
        </Button>
        <Tooltip title={canSubmit ? '' : '변경할 옵션과 변경 사유를 모두 입력해 주세요.'}>
          <span>
            <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
              변경하기
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

export default OptionChangeModal;
